import type { ReactNode } from 'react';
import { MultiToggle } from './Panel';
import './SettingsMenu.css';

/*
 * Legacy settings-menu components. Kept alive only for Creator / CreatorSetup,
 * which import from this file directly. New code should use SettingRow + Switch
 * from ./SettingRow.tsx + ./Switch.tsx and compose inside a Popup instead.
 */

interface SettingsRowProps {
  label: ReactNode;
  description?: ReactNode;
  control?: ReactNode;
  className?: string;
  locked?: boolean;
  onClick?: () => void;
  role?: string;
  'aria-checked'?: boolean;
}

export function SettingsRow({
  label,
  description,
  control,
  className,
  locked = false,
  onClick,
  role,
  'aria-checked': ariaChecked,
}: SettingsRowProps) {
  const rowClassName = [
    'ui-settings-row',
    onClick && !locked ? 'ui-settings-row--interactive' : '',
    locked ? 'ui-settings-row--locked' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      <div className="ui-settings-row__info">
        <span className="ui-settings-row__label">{label}</span>
        {description && <span className="ui-settings-row__desc">{description}</span>}
      </div>
      {control}
    </>
  );

  if (onClick && !locked) {
    return (
      <button
        type="button"
        className={rowClassName}
        onClick={onClick}
        role={role}
        aria-checked={ariaChecked}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={rowClassName} aria-disabled={locked || undefined}>
      {content}
    </div>
  );
}

interface SettingsSwitchProps {
  checked: boolean;
  className?: string;
}

export function SettingsSwitch({ checked, className }: SettingsSwitchProps) {
  return (
    <div
      className={`ui-settings-switch ${checked ? 'is-on' : ''} ${className ?? ''}`.trim()}
      aria-hidden="true"
    >
      <div className="ui-settings-switch__knob" />
    </div>
  );
}

interface SettingsToggleRowProps {
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  className?: string;
  locked?: boolean;
  onChange?: (checked: boolean) => void;
}

export function SettingsToggleRow({
  label,
  description,
  checked,
  className,
  locked = false,
  onChange,
}: SettingsToggleRowProps) {
  return (
    <SettingsRow
      label={label}
      description={description}
      control={<SettingsSwitch checked={checked} />}
      className={className}
      locked={locked}
      onClick={onChange ? () => onChange(!checked) : undefined}
      role="switch"
      aria-checked={checked}
    />
  );
}

interface SettingsChoiceOption<T extends string | number = string | number> {
  value: T;
  label: string;
}

interface SettingsChoiceRowProps<T extends string | number = string | number> {
  label: ReactNode;
  description?: ReactNode;
  options: SettingsChoiceOption<T>[];
  value: T;
  className?: string;
  locked?: boolean;
  compact?: boolean;
  onChange?: (value: T) => void;
}

export function SettingsChoiceRow<T extends string | number = string | number>({
  label,
  description,
  options,
  value,
  className,
  locked = false,
  compact = false,
  onChange,
}: SettingsChoiceRowProps<T>) {
  return (
    <SettingsRow
      label={label}
      description={description}
      className={`ui-settings-row--wrap ${className ?? ''}`.trim()}
      locked={locked}
      control={
        <MultiToggle
          options={options}
          value={value}
          onChange={(nextValue) => onChange?.(nextValue)}
          disabled={locked || !onChange}
          compact={compact}
        />
      }
    />
  );
}
